import React, { useState, useEffect } from 'react';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import { Button } from '@progress/kendo-react-buttons';
import { useNavigate } from 'react-router-dom';
import { invoices as mockInvoices } from '../../data/mockData';
import { invoiceApi } from '../../api/invoiceApi';
import { isApiConfigured } from '../../api/axiosClient';
import { LoadingSpinner, ErrorCard } from '../../components/LoadingSpinner';

const InvoiceListPage = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState({ skip: 0, take: 5 });
  const [sort, setSort] = useState([]);
  const [invoices, setInvoices] = useState(mockInvoices);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isApiConfigured()) {
      setLoading(true);
      setError(null);
      invoiceApi.getInvoices()
        .then((res) => setInvoices(res.data))
        .catch((err) => setError(err.message || 'Failed to load invoices'))
        .finally(() => setLoading(false));
    }
  }, []);

  /* -- derived stats ------------------------------------------------------ */
  const totalInvoices = invoices.length;
  const pendingCount = invoices.filter((i) => i.status === 'Pending').length;
  const overdueCount = invoices.filter((i) => i.status === 'Overdue').length;
  const amountDue = invoices
    .filter((i) => i.status === 'Pending' || i.status === 'Overdue')
    .reduce((sum, i) => sum + i.amount, 0);

  /* -- grid data with sorting + paging ------------------------------------ */
  const sortedData = [...invoices].sort((a, b) => {
    if (!sort.length) return 0;
    const { field, dir } = sort[0];
    const aVal = a[field];
    const bVal = b[field];
    if (aVal < bVal) return dir === 'asc' ? -1 : 1;
    if (aVal > bVal) return dir === 'asc' ? 1 : -1;
    return 0;
  });

  const pagedData = sortedData.slice(page.skip, page.skip + page.take);

  /* -- cell renderers ----------------------------------------------------- */
  const StatusCell = (props) => {
    const status = props.dataItem.status;
    const cls = status === 'Paid' ? 'paid' : status === 'Pending' ? 'pending' : status === 'Overdue' ? 'overdue' : '';
    return (
      <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
        <span className={`status-badge ${cls}`}>{status}</span>
      </td>
    );
  };

  const AmountCell = (props) => (
    <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
      ${props.dataItem.amount.toFixed(2)}
    </td>
  );

  const ActionCell = (props) => {
    const { status } = props.dataItem;
    if (status === 'Pending' || status === 'Overdue') {
      return (
        <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
          <Button themeColor="primary" size="small" onClick={() => alert(`Pay Now for ${props.dataItem.claimNumber}`)}>
            Pay Now
          </Button>
        </td>
      );
    }
    return (
      <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
        <Button fillMode="outline" size="small" onClick={() => alert(`View ${props.dataItem.claimNumber}`)}>
          View
        </Button>
      </td>
    );
  };

  const ServiceDateCell = (props) => {
    const d = new Date(props.dataItem.dateOfService + 'T00:00:00');
    return (
      <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
        {d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}
      </td>
    );
  };

  return (
    <div>
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1>Invoices & Billing</h1>
          <p>View and manage outstanding invoices and payments</p>
        </div>
        <button
          className="btn-secondary"
          onClick={() => navigate('/invoices/payments')}
        >
          View Payment History
        </button>
      </div>

      {loading && <LoadingSpinner message="Loading invoices..." />}
      {error && <ErrorCard message={error} onRetry={() => window.location.reload()} />}

      {!loading && !error && (
      <div>
      {/* Stat Cards */}
      <div className="stat-cards">
        <div className="stat-card">
          <div className="stat-icon teal">{'\u{1F9FE}'}</div>
          <div className="stat-value">{totalInvoices}</div>
          <div className="stat-label">Total Invoices</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon orange">{'\u23F3'}</div>
          <div className="stat-value">{pendingCount}</div>
          <div className="stat-label">Pending</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon danger">{'\u26A0\uFE0F'}</div>
          <div className="stat-value">{overdueCount}</div>
          <div className="stat-label">Overdue</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon purple">{'\u{1F4B0}'}</div>
          <div className="stat-value">
            ${amountDue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </div>
          <div className="stat-label">Amount Due</div>
        </div>
      </div>

      {/* Kendo Grid */}
      {invoices.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.5 }}>{'\u{1F4CB}'}</div>
          <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
            No Invoices Found
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
            There are no invoices to display at this time
          </div>
        </div>
      ) : (
        <div className="grid-scroll-wrapper">
          <Grid
            data={pagedData}
            sortable={true}
            sort={sort}
            onSortChange={(e) => setSort(e.sort)}
            pageable={{ pageSizes: false }}
            skip={page.skip}
            take={page.take}
            total={invoices.length}
            onPageChange={(e) => setPage({ skip: e.page.skip, take: e.page.take })}
          >
            <GridColumn field="claimNumber" title="Claim #" width="160px" />
            <GridColumn field="description" title="Description" width="200px" />
            <GridColumn field="memberName" title="Member Name" width="170px" />
            <GridColumn
              field="dateOfService"
              title="Service Date"
              cells={{ data: ServiceDateCell }}
              width="130px"
            />
            <GridColumn
              field="amount"
              title="Amount ($)"
              cells={{ data: AmountCell }}
              width="120px"
            />
            <GridColumn
              field="status"
              title="Status"
              cells={{ data: StatusCell }}
              width="150px"
            />
            <GridColumn
              title="Actions"
              cells={{ data: ActionCell }}
              sortable={false}
              width="120px"
            />
          </Grid>
        </div>
      )}
      </div>
      )}
    </div>
  );
};

export default InvoiceListPage;
